import {
  ChangeEventHandler,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  RefObject,
  forwardRef,
  memo,
  useRef
} from 'react';
import '../styles.css';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  label?: string;
  isError?: boolean;
  errorMessage?: string;
  /** Высота иконки должна быть не более line-height текста инпута */
  leftIcon?: ReactNode;
  /** Высота иконки должна быть не более line-height текста инпута */
  rightIcon?: ReactNode;
  /** Лучше всего использовать формат регулярного выражения с '^', '$', и флагом m */
  regexAllowedSymbols?: RegExp;
  value?: string;
  /** Пропсы для компонента-рамки вокруг инпута - "видимый" инпут */
  inputFrameProps?: HTMLAttributes<HTMLDivElement>;
  /** Пропсы для внешней обертки, которая содержит все дополнительные элементы вместе инпутом */
  inputWrapperProps?: HTMLAttributes<HTMLDivElement>;
  /** Функция которая меняет вводимое значение по мере написания. Принимает актуальный value и отдает измененное значение.
   * Если форматтер отдает символ который запрещен через regexAllowedSymbols, то он НЕ будет выведен => меняйте regexAllowedSymbols
   */
  formatter?: (value: string) => string;
  inputFrameClassName?: string;
}

const _Input = forwardRef<HTMLInputElement, InputProps>(function _Input(
  {
    label,
    value,
    onChange,
    errorMessage,
    leftIcon,
    rightIcon,
    disabled,
    className,
    isError,
    inputFrameClassName,
    inputFrameProps,
    inputWrapperProps,
    regexAllowedSymbols,
    formatter,
    ...props
  },
  _ref
) {
  const innerRef = useRef<HTMLInputElement>(null);
  const ref = (_ref || innerRef) as RefObject<HTMLInputElement>;
  const { t } = useTranslation();

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (regexAllowedSymbols) {
      event.target.value = event.target.value.replace(regexAllowedSymbols, '');
    }

    if (formatter) {
      event.target.value = formatter(event.target.value);
    }

    onChange?.(event);
  };

  return (
    <div
      data-role="input"
      {...inputWrapperProps}
      className={twMerge('flex flex-col gap-2 w-full', inputWrapperProps?.className)}>
      {label && <label className="text-base text-i_label pl-2.5">{label}</label>}
      <div
        {...inputFrameProps}
        onClick={(e) => {
          if (disabled || props.readOnly) return;
          ref.current?.focus();
          inputFrameProps?.onClick?.(e);
        }}
        className={twMerge(
          'flex gap-2 border-solid rounded-[20px] pl-[15px] pr-2.5 py-2.5 border border-i_border max-h-10 bg-i_bg w-full justify-between items-center cursor-text',
          inputFrameProps?.className,
          (isError || Boolean(errorMessage)) && 'border-i_border_error',
          (disabled || props.readOnly) && 'bg-i_bg_disabled cursor-not-allowed',
          inputFrameClassName
        )}>
        {leftIcon}
        <input
          ref={ref}
          type="text"
          {...props}
          disabled={disabled}
          value={value}
          onChange={handleChange}
          className={twMerge(
            'text-base flex-1 placeholder:text-base placeholder:text-i_placeholder text-i_text disabled:!opacity-100 bg-i_bg w-full',
            (disabled || props.readOnly) &&
              'bg-i_bg_disabled text-i_text_disabled cursor-not-allowed',
            className
          )}
        />
        {rightIcon && (
          <div
            className={twMerge(
              ' transition-transform text-i_icon',
              disabled || props.readOnly ? 'cursor-not-allowed' : 'cursor-pointer'
            )}>
            {rightIcon}
          </div>
        )}
      </div>
      {errorMessage && <span className="text-map_error text-xs">{t(errorMessage)}</span>}
    </div>
  );
});

export const Input = memo(_Input);
